import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { splitHint } from '@/shared/lib/hint';
import s from './tooltip.module.css';

const DELAY = 450;
/** Пока курсор идёт по панели значков, следующая подсказка открывается сразу и без анимации (как в macOS и Linear). */
const WARM = 600;
const GAP = 8;
const MARGIN = 6;

interface Tip {
  readonly text: string;
  readonly keys: readonly string[];
  /** Строки под заголовком — серым (у статуса: «До завтра, 08:00»). */
  readonly sub: readonly string[];
  /** Эмодзи слева крупно (у статуса). */
  readonly emoji?: string | undefined;
  readonly top: number;
  readonly left: number;
  /** Где подсказка относительно элемента: сверху (по умолчанию), снизу или справа (полоса разделов, как в Slack). */
  readonly side: 'above' | 'below' | 'right';
  /** Куда рисовать: в открытое модальное окно (иначе подсказка окажется под ним) или в body. */
  readonly host: Element;
  /** Соседняя подсказка сразу после предыдущей — появляется без анимации. */
  readonly warm: boolean;
}

/**
 * Подсказки при наведении, как в Slack: тёмная плашка над элементом, клавиши — отдельными «кнопками».
 * Берутся из обычного title, поэтому работают везде; системная подсказка на это время прячется.
 * На сенсорных экранах подсказок нет.
 */
export function Tooltips() {
  const [tip, setTip] = useState<Tip | null>(null);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function' || !window.matchMedia('(hover: hover)').matches) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let owner: HTMLElement | null = null;
    let shown = false;
    let warmUntil = 0;

    const restore = (keepWarm = false) => {
      clearTimeout(timer);
      // Только что показанная подсказка «прогревает» соседей; пробег курсора по пустому месту прогрев не сбрасывает.
      if (shown) warmUntil = keepWarm ? performance.now() + WARM : 0;
      shown = false;
      if (owner !== null) {
        const saved = owner.dataset.tip;
        if (saved !== undefined && !owner.hasAttribute('title')) owner.setAttribute('title', saved);
        if (owner.dataset.tipLabel !== undefined) owner.removeAttribute('aria-label');
        delete owner.dataset.tip;
        delete owner.dataset.tipLabel;
      }
      owner = null;
      setTip(null);
    };

    // Слои не наслаиваем: пока у кнопки открыто меню или поповер, её подсказка не нужна, а пока открыто меню — подсказки вне его тоже.
    const covered = (el: Element) => {
      if (el.closest('[aria-expanded="true"]') !== null) return true;
      const menu = document.querySelector('[role="menu"]');
      return menu !== null && !menu.contains(el);
    };

    const show = (el: HTMLElement, title: string, warm = false) => {
      if (!el.isConnected || covered(el)) return;
      shown = true;
      const r = el.getBoundingClientRect();
      // Многострочная подсказка: первая строка — заголовок, остальные — серым под ним.
      const [first = '', ...sub] = title.split('\n');
      const { text, keys } = splitHint(first);
      const emoji = el.dataset.tipEmoji;
      const host = el.closest('dialog[open]') ?? document.body;
      if (el.closest('[data-tip-side="right"]') !== null) {
        setTip({ text, keys, sub, emoji, top: r.top + r.height / 2, left: r.right + GAP, side: 'right', host, warm });
        return;
      }
      if (el.closest('[data-tip-side="below"]') !== null) {
        setTip({ text, keys, sub, emoji, top: r.bottom + GAP, left: r.left + r.width / 2, side: 'below', host, warm });
        return;
      }
      // Многострочной подсказке над значком нужно больше места: у верхнего края она уходит вниз.
      const below = r.top < 48 + sub.length * 40;
      setTip({ text, keys, sub, emoji, top: below ? r.bottom + GAP : r.top - GAP, left: r.left + r.width / 2, side: below ? 'below' : 'above', host, warm });
    };

    const onOver = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const target = e.target instanceof Element ? e.target.closest<HTMLElement>('[title], [data-tip]') : null;
      if (target === owner) return;
      const warm = performance.now() < warmUntil;
      restore();
      if (target === null) return;
      const title = target.getAttribute('title') ?? target.dataset.tip ?? '';
      if (title.trim() === '') return;
      // Прячем системную подсказку, пока показываем свою.
      owner = target;
      target.dataset.tip = title;
      target.removeAttribute('title');
      // Без title кнопка-значок осталась бы без имени для чтения с экрана — подставляем его на время.
      if (!target.hasAttribute('aria-label') && !target.hasAttribute('aria-labelledby') && target.textContent.trim() === '') {
        target.setAttribute('aria-label', title);
        target.dataset.tipLabel = '1';
      }
      // Подсказка, повторяющая видимую подпись («Вчера» на плашке «Вчера»), только мешает — не показываем
      // (и системную тоже: title уже снят на время наведения).
      const visible = target.textContent.replace(/\s+/g, ' ').trim();
      if (visible !== '' && visible === title.replace(/\s+/g, ' ').trim()) return;
      if (warm) {
        show(target, title, true);
        return;
      }
      timer = setTimeout(() => {
        show(target, title);
      }, DELAY);
    };
    const onOut = (e: PointerEvent) => {
      if (owner === null) return;
      const to = e.relatedTarget;
      if (to instanceof Node && owner.contains(to)) return;
      restore(true);
    };

    // Щелчок, клавиша, прокрутка — подсказка уходит, и следующая снова ждёт задержку.
    const reset = () => {
      warmUntil = 0;
      restore();
    };
    // Меню открылось, пока подсказка уже видна (меню по наведению появляется позже подсказки) — подсказку убираем сразу.
    const observer = new MutationObserver(() => {
      if (shown && owner !== null && covered(owner)) restore();
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
    document.addEventListener('pointerover', onOver, true);
    document.addEventListener('pointerout', onOut, true);
    document.addEventListener('pointerdown', reset, true);
    document.addEventListener('keydown', reset, true);
    window.addEventListener('scroll', reset, true);
    window.addEventListener('blur', reset);
    window.addEventListener('hashchange', reset);
    window.addEventListener('popstate', reset);
    return () => {
      restore();
      observer.disconnect();
      document.removeEventListener('pointerover', onOver, true);
      document.removeEventListener('pointerout', onOut, true);
      document.removeEventListener('pointerdown', reset, true);
      document.removeEventListener('keydown', reset, true);
      window.removeEventListener('scroll', reset, true);
      window.removeEventListener('blur', reset);
      window.removeEventListener('hashchange', reset);
      window.removeEventListener('popstate', reset);
    };
  }, []);

  if (tip === null) return null;
  return createPortal(<TipBox tip={tip} />, tip.host);
}

function TipBox({ tip }: { readonly tip: Tip }) {
  return (
    <div
      role="tooltip"
      className={[s.tip, tip.side === 'below' ? s.below : tip.side === 'right' ? s.right : '', tip.warm ? s.warm : ''].join(' ')}
      style={{ top: tip.top, left: tip.left }}
      ref={(el) => {
        if (el === null) return;
        if (tip.side === 'right') return;
        // Не вылезать за края окна: сдвигаем середину подсказки, если надо.
        const half = el.offsetWidth / 2;
        const center = Math.min(Math.max(tip.left, MARGIN + half), window.innerWidth - MARGIN - half);
        el.style.left = `${String(center)}px`;
      }}
    >
      {tip.emoji !== undefined && <span className={s.emoji}>{tip.emoji}</span>}
      {tip.sub.length > 0 || tip.emoji !== undefined ? (
        <span className={s.lines}>
          <span className={s.title}>{tip.text}</span>
          {tip.sub.map((line, i) =>
            line === '---' ? (
              <span key={`r${String(i)}`} className={s.rule} role="separator" />
            ) : (
              <span key={line} className={s.sub}>
                {line}
              </span>
            ),
          )}
        </span>
      ) : (
        <span>{tip.text}</span>
      )}
      {tip.keys.length > 0 && (
        <span className={s.keys}>
          {tip.keys.map((k) => (
            <kbd key={k} className={s.key}>
              {k}
            </kbd>
          ))}
        </span>
      )}
    </div>
  );
}
